<script setup lang="ts">
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosButton from '@/components/ui/FosButton.vue'
import CoordinateInput from '@/components/ui/CoordinateInput.vue'
import { useWizardStore } from '@/stores/wizard'
import { SUBJECT_CATEGORIES, MOBILITY_OPTIONS, TERRAIN_OPTIONS } from '@/types'

const wizard = useWizardStore()

function toggleTerrain(terrain: string) {
  const idx = wizard.step2.terrain_types.indexOf(terrain)
  if (idx === -1) {
    wizard.step2.terrain_types.push(terrain)
  } else {
    wizard.step2.terrain_types.splice(idx, 1)
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Subject & Situation</h2>
      <p class="font-mono text-xs text-outline mt-1">Step 2 of 4 — optional</p>
    </div>

    <!-- Name -->
    <div class="flex gap-3">
      <div class="flex flex-col gap-1.5 flex-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">First name</label>
        <FosInput v-model="wizard.step2.subject_first_name" placeholder="First" />
      </div>
      <div class="flex flex-col gap-1.5 flex-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">Last name</label>
        <FosInput v-model="wizard.step2.subject_last_name" placeholder="Last" />
      </div>
    </div>

    <!-- Age + Category -->
    <div class="flex gap-3">
      <div class="flex flex-col gap-1.5 w-32">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">Age</label>
        <input
          type="number"
          min="0"
          max="120"
          placeholder="—"
          :value="wizard.step2.age ?? ''"
          class="w-full px-3 py-2 bg-surface-container-high text-on-surface font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 placeholder:text-outline/50"
          @input="wizard.step2.age = ($event.target as HTMLInputElement).value ? Number(($event.target as HTMLInputElement).value) : null"
        />
      </div>
      <div class="flex flex-col gap-1.5 flex-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">Category</label>
        <FosSelect
          :model-value="wizard.step2.subject_category ?? undefined"
          :options="SUBJECT_CATEGORIES"
          placeholder="Select category"
          @update:model-value="wizard.step2.subject_category = $event"
        />
      </div>
    </div>

    <!-- Mobility -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">Mobility</label>
      <FosSelect
        :model-value="wizard.step2.mobility ?? undefined"
        :options="MOBILITY_OPTIONS"
        placeholder="Select mobility"
        @update:model-value="wizard.step2.mobility = $event"
      />
    </div>

    <!-- LKP -->
    <CoordinateInput
      :lat="wizard.step2.lkp_lat"
      :lng="wizard.step2.lkp_lng"
      label="Last Known Point (LKP)"
      @update:coords="wizard.step2.lkp_lat = $event.lat; wizard.step2.lkp_lng = $event.lng"
    />

    <!-- Terrain types -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Terrain type <span class="font-normal normal-case tracking-normal text-outline/50">(select all that apply)</span>
      </label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="t in TERRAIN_OPTIONS"
          :key="t"
          type="button"
          class="px-3 py-1.5 font-mono text-xs uppercase tracking-widest font-bold transition-colors focus:outline-none"
          :class="
            wizard.step2.terrain_types.includes(t)
              ? 'bg-primary-container text-white'
              : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
          "
          @click="toggleTerrain(t)"
        >
          {{ t }}
        </button>
      </div>
    </div>

    <!-- Situation summary -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Situation summary
      </label>
      <FosTextarea
        v-model="wizard.step2.situation_summary"
        placeholder="Brief summary for the Incident Action Plan..."
        :rows="3"
      />
    </div>

    <!-- Navigation -->
    <div class="flex justify-between pt-2">
      <FosButton variant="secondary" @click="wizard.currentStep = 1">← Back</FosButton>
      <div class="flex gap-2">
        <FosButton variant="secondary" @click="wizard.currentStep = 3">Skip</FosButton>
        <FosButton @click="wizard.currentStep = 3">Next →</FosButton>
      </div>
    </div>
  </div>
</template>
